/**
 * Bstory — Product Closeup play/pause overlay.
 *
 * The section deliberately ships the video with native `controls` off, so this
 * adds the only control the design has: one central button that toggles
 * playback and swaps its own icon.
 *
 * STATE COMES FROM THE VIDEO, NOT FROM THE CLICK. `data-video-state` on the
 * card is written by the real `play` / `pause` / `ended` events, so a blocked
 * or interrupted play can never leave a stale icon. CSS does the rest.
 *
 * The first click is also handled by <hdt-video-player>'s own one-shot click
 * listener (that is what starts playback and hides the poster facade). This
 * module deliberately does NOT call play() on that first click — it only
 * pauses/resumes once playback has begun, so the two never fight.
 *
 * Scoped per card, bound in connectedCallback-equivalent fashion on the
 * element itself with an AbortController, so Theme Editor section reloads
 * cannot accumulate listeners.
 */

const CARD_SELECTOR = '.hdt-bstory-product-closeup__card';
const TOGGLE_SELECTOR = '[data-bstory-closeup-toggle]';
const BOUND = 'bstoryCloseupBound';

function setState(card, state) {
  card.dataset.videoState = state;

  const toggle = card.querySelector(TOGGLE_SELECTOR);
  if (!toggle) return;

  const label =
    state === 'playing'
      ? toggle.dataset.labelPause
      : toggle.dataset.labelPlay;

  if (label && toggle.getAttribute('aria-label') !== label) {
    toggle.setAttribute('aria-label', label);
  }
}

function bindCard(card) {
  if (card.dataset[BOUND] === 'true') return;

  const video = card.querySelector('video');
  const toggle = card.querySelector(TOGGLE_SELECTOR);
  if (!video || !toggle) return;

  card.dataset[BOUND] = 'true';
  const controller = new AbortController();
  const { signal } = controller;

  setState(card, video.paused ? 'paused' : 'playing');

  video.addEventListener('play', () => setState(card, 'playing'), { signal });
  video.addEventListener('pause', () => setState(card, 'paused'), { signal });
  video.addEventListener('ended', () => setState(card, 'paused'), { signal });

  const player = card.querySelector('hdt-video-player');

  /**
   * A real toggle, decided from the ELEMENT's own state rather than a cached
   * flag or the last-written class, so it can never drift out of sync.
   */
  function togglePlayback() {
    if (video.paused || video.ended) {
      if (video.ended) video.currentTime = 0;
      const played = video.play();
      // A rejected play() (autoplay policy, decode error) must not leave the
      // icon claiming it is playing.
      if (played && typeof played.catch === 'function') {
        played.catch(() => setState(card, video.paused ? 'paused' : 'playing'));
      }
    } else {
      video.pause();
    }
  }

  toggle.addEventListener(
    'click',
    (event) => {
      // FIRST click only: <hdt-video-player> registers its own listener with
      // `{ once: true }`, and that is what creates the player and sets
      // [loaded]. Let that one through, then own every click afterwards —
      // previously this handler deferred on EVERY non-playing click, so after
      // the first pause the spent one-shot listener meant nothing could
      // restart playback.
      if (player && !player.hasAttribute('loaded')) return;

      event.preventDefault();
      event.stopPropagation();
      togglePlayback();
    },
    { signal }
  );

  // Tapping the media itself toggles too, which is the touch equivalent of the
  // desktop hover-then-click. Only once playing, so the first tap still goes to
  // the player's own start handler.
  video.addEventListener(
    'click',
    () => {
      if (player && !player.hasAttribute('loaded')) return;
      togglePlayback();
    },
    { signal }
  );

  card._bstoryCloseupAbort = controller;
}

function init(scope) {
  (scope || document).querySelectorAll(CARD_SELECTOR).forEach(bindCard);
}

init();

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll(CARD_SELECTOR).forEach((card) => {
    if (card._bstoryCloseupAbort) card._bstoryCloseupAbort.abort();
  });
});

document.addEventListener('shopify:section:load', (event) => {
  init(event.target);
});
